import type { ReactNode } from 'react';
import { GraduationCap, School, UserRound, type LucideIcon } from 'lucide-react';
import { EmptyValue } from '@/components/common/EmptyValue';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatDisplayDate } from '@/utils/date';
import {
  TEACHER_CARD_CLASS,
  TEACHER_GENDER_LABELS,
  TEACHER_MARITAL_STATUS_LABELS,
  TEACHING_MEDIUM_LABELS,
} from '../constants/teacherSubmission.constants';
import type { TeacherDetail } from '../types/teacherSubmission.types';

const GROUP_HEADER_CELL_CLASS = 'bg-info/60 px-4 py-2.5 text-left';
const LABEL_CELL_CLASS =
  'w-36 border-r px-4 py-2 text-left align-top font-normal text-muted-foreground sm:w-48';
const VALUE_CELL_CLASS = 'px-4 py-2 font-medium wrap-break-word';

interface DetailRowConfig {
  label: string;
  getValue: (teacher: TeacherDetail) => ReactNode;
}

interface DetailGroupConfig {
  icon: LucideIcon;
  title: string;
  rows: DetailRowConfig[];
}

const DETAIL_GROUPS: DetailGroupConfig[] = [
  {
    icon: UserRound,
    title: 'Personal',
    rows: [
      {
        label: 'Gender',
        getValue: (teacher) => teacher.gender && TEACHER_GENDER_LABELS[teacher.gender],
      },
      {
        label: 'Date of birth',
        getValue: (teacher) => teacher.dateOfBirth && formatDisplayDate(teacher.dateOfBirth),
      },
      {
        label: 'Marital status',
        getValue: (teacher) =>
          teacher.maritalStatus && TEACHER_MARITAL_STATUS_LABELS[teacher.maritalStatus],
      },
      { label: 'NID', getValue: (teacher) => teacher.nidNumber },
      { label: 'Registration ID', getValue: (teacher) => teacher.registrationId },
    ],
  },
  {
    icon: School,
    title: 'Teaching',
    rows: [
      { label: 'Priority', getValue: (teacher) => teacher.teacherPriority },
      {
        label: 'Medium',
        getValue: (teacher) =>
          teacher.teachingMedium && TEACHING_MEDIUM_LABELS[teacher.teachingMedium],
      },
      {
        label: 'Teaching since',
        getValue: (teacher) =>
          teacher.teachingStartDate && formatDisplayDate(teacher.teachingStartDate),
      },
      {
        label: 'Retirement',
        getValue: (teacher) => teacher.retirementDate && formatDisplayDate(teacher.retirementDate),
      },
    ],
  },
  {
    icon: GraduationCap,
    title: 'Education',
    rows: [
      { label: 'Highest degree', getValue: (teacher) => teacher.highestDegree },
      { label: 'Institution', getValue: (teacher) => teacher.passingInstitute },
      {
        label: 'Passed on',
        getValue: (teacher) => teacher.passingDate && formatDisplayDate(teacher.passingDate),
      },
    ],
  },
];

interface GroupHeaderProps {
  icon: LucideIcon;
  title: string;
}

function GroupHeader({ icon: Icon, title }: GroupHeaderProps) {
  return (
    <tr>
      <th colSpan={2} scope="colgroup" className={GROUP_HEADER_CELL_CLASS}>
        <span className="flex items-center gap-2 font-semibold">
          <Icon className="size-4 text-muted-foreground" aria-hidden />
          {title}
        </span>
      </th>
    </tr>
  );
}

interface TeacherDetailsTableProps {
  teacher: TeacherDetail;
  className?: string;
}

export function TeacherDetailsTable({ teacher, className }: TeacherDetailsTableProps) {
  return (
    <section className={cn(TEACHER_CARD_CLASS, className)}>
      <table className="w-full table-fixed text-sm">
        {DETAIL_GROUPS.map((group) => (
          <tbody key={group.title} className="border-b last:border-b-0">
            <GroupHeader icon={group.icon} title={group.title} />
            {group.rows.map((row) => (
              <tr key={row.label} className="border-t">
                <th scope="row" className={LABEL_CELL_CLASS}>
                  {row.label}
                </th>
                <td className={VALUE_CELL_CLASS}>{row.getValue(teacher) ?? <EmptyValue />}</td>
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </section>
  );
}

export function TeacherDetailsTableSkeleton() {
  return (
    <div className={TEACHER_CARD_CLASS} aria-hidden>
      <table className="w-full table-fixed text-sm">
        {DETAIL_GROUPS.map((group) => (
          <tbody key={group.title} className="border-b last:border-b-0">
            <tr>
              <td colSpan={2} className={GROUP_HEADER_CELL_CLASS}>
                <Skeleton className="h-4 w-28" />
              </td>
            </tr>
            {group.rows.map((row) => (
              <tr key={row.label} className="border-t">
                <td className={LABEL_CELL_CLASS}>
                  <Skeleton className="h-4 w-24" />
                </td>
                <td className={VALUE_CELL_CLASS}>
                  <Skeleton className="h-4 w-32" />
                </td>
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}
